import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Snackbar, Alert, CircularProgress } from "@mui/material";

const EditMembership = () => {
  const location = useLocation(); // Get the state passed via navigation
  const membershipToEdit = location.state?.plans; // Extract the membership data passed via state
  const [formValues, setFormValues] = useState({
    amount_paid: "",
    title: "",
    membership_features: [],
    term: "",
  });
  const [newFeature, setNewFeature] = useState(""); // State to track the input for a new feature
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [touched, setTouched] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  useEffect(() => {
    if (membershipToEdit) {
      // If membership data exists in state, populate form fields
      setFormValues({
        ...membershipToEdit,
        membership_features: membershipToEdit.membership_features.split(", "), // Convert comma-separated string back to array
      });
    } else {
      setError("No membership data found.");
      setSnackbarOpen(true);
    }
  }, [membershipToEdit]);

  const handleAddFeature = () => {
    if (newFeature.trim() !== "") {
      setFormValues({
        ...formValues,
        membership_features: [
          ...formValues.membership_features,
          newFeature.trim(),
        ],
      });
      setNewFeature(""); // Clear the input field
    } else {
      setFormError("Feature cannot be empty.");
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    setFormValues({
      ...formValues,
      membership_features: formValues.membership_features.filter(
        (feature) => feature !== featureToRemove
      ),
    });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        // console.log("Access required");
        return;
      }

      // Validate if at least one membership feature is added
      if (formValues.membership_features.length === 0) {
        setLoading(false);
        setSnackbarMessage("Please add at least one membership feature.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      // Convert membership_features array to comma-separated string
      const featuresString = formValues.membership_features.join(", ");

      const response = await axios.put(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/update-membership/${membershipToEdit._id}`,
        {
          ...formValues,
          membership_features: featuresString,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setSnackbarMessage("Updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
          navigate("/membership-list");
        }, 2000);
      } else {
        setFormError(response.data.message);
      }
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 400) {
        setSnackbarMessage("Please fill out all required fields...");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Failed to update. Please try again");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  return (
    <div className="row gy-4">
      <div className="col-lg-8" style={styles.cardContent(loading)}>
        <div className="card h-100">
          <div className="card-body p-24">
            <h6 className="text-md text-primary-light mb-16">
              Edit Membership
            </h6>

            <form onSubmit={(e)=>handleFormSubmit(e)}>
              {/* Membership Name */}
              <div className="mb-24">
                <label
                  htmlFor="batch_name"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Title <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="title"
                  placeholder="Enter Membership Name"
                  required
                  value={formValues.title}
                  onChange={(e) =>{
                    const value = e.target.value;
                    if (value === "" || value.trim() !== "") {
                    setFormValues({ ...formValues, title: e.target.value })
                    }
                  }
                  }
                  
                />
              </div>

              {/* Batch Description */}
              <div className="mb-24">
                <label
                  htmlFor="amount_paid"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Amount Paid <span className="text-danger-600">*</span>
                </label>
                <input
                  type="number"
                  className="form-control radius-8"
                  id="amount_paid"
                  required
                  placeholder="Enter Amount Paid"
                  value={formValues.amount_paid}
                  onChange={(e) => {
                    const regex = /^[0-9]*$/; // Only allow numeric values
                    const value = e.target.value;

                    if (regex.test(value)) {
                      setFormValues({ ...formValues, amount_paid: value });
                    }
                  }}
                  onBlur={() => setTouched(true)} // Mark as touched when losing focus
                />
              </div>

              {/* Term */}
              <div className="mb-24">
                <label
                  htmlFor="term"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Term <span className="text-danger-600">*</span>
                </label>
                <select
                  className="form-control radius-8"
                  id="term"
                  value={formValues.term}
                  required
                  onChange={(e) =>
                    setFormValues({ ...formValues, term: e.target.value })
                  }
                >
                  <option value="" disabled>
                    Select Term
                  </option>
                  <option value="Yearly">Yearly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>

              {/*  Membership Feature */}
              <div className="mb-24">
                <label
                  htmlFor="membership_features"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Add Membership Feature{" "}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  className="form-control radius-8"
                  id="membership_features"
                  value={newFeature}
                  onChange={(e) =>{
                    const value = e.target.value;
                    if (value === "" || value.trim() !== "") {
                     setNewFeature(e.target.value)}
                    }
                    }
                />
                {formError && (
                  <div className="text-danger-600">{formError}</div>
                )}

                {/* Add Button */}
                <button
                  className="btn btn-outline-primary mt-2"
                  onClick={handleAddFeature}
                  disabled={loading || newFeature.trim() === ""}
                >
                  Add Feature
                </button>

                {/* Display Added Features */}
                <div className="mt-3">
                  <p>Added Features:</p>
                  <div className="d-flex flex-wrap">
                    {formValues?.membership_features?.map((feature, index) =>
                      feature ? (
                        <div
                          key={index}
                          className="chip"
                          style={{
                            marginRight: "8px",
                            marginBottom: "8px",
                            backgroundColor: "#f0f0f0",
                            padding: "6px 12px",
                            borderRadius: "16px",
                            color: "#000000",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <span>{feature}</span>
                          <button
                            className="btn btn-sm btn-danger ml-2"
                            onClick={() => handleRemoveFeature(feature)}
                            style={{
                              borderRadius: "50%",
                              padding: "0 6px",
                              fontSize: "14px",
                            }}
                          >
                            x
                          </button>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex align-items-center justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                  onClick={() => navigate("/membership-list")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                  // onClick={handleFormSubmit}
                  disabled={loading}
                >
                  {loading && (
                    <div
                      className="loader-container"
                      style={styles.loaderContainer}
                    >
                      <CircularProgress />
                    </div>
                  )}
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          // variant="filled"
          // sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

// Add CSS for loader
const styles = {
  loaderContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
  },
  cardContent: (loading) => ({
    opacity: loading ? 0.5 : 1,
  }),
};

export default EditMembership;
